import React, { useEffect, useRef, useState } from "react";
import { api, fileToBase64 } from "@/lib/api";
import { useSession } from "@/lib/session";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ShieldCheck,
  ScanLine,
  AlertTriangle,
  Check,
  Layers,
  Camera,
} from "lucide-react";

export default function Kronos() {
  const { shift } = useSession();
  const camRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [nextPrompt, setNextPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [audits, setAudits] = useState([]);
  const [latest, setLatest] = useState(null);
  const [commitToShift, setCommitToShift] = useState(true);

  const loadAudits = () =>
    api
      .get("/kronos/audits")
      .then(({ data }) => setAudits(data?.audits || data || []))
      .catch(() => setAudits([]));

  useEffect(() => {
    loadAudits();
  }, []);

  const onPageCaptured = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      setPages((p) => [...p, b64]);
      setNextPrompt(true);
    } catch {
      toast.error("Could not read image");
    }
  };

  const askNext = () => {
    setNextPrompt(false);
    setTimeout(() => camRef.current?.click(), 80);
  };

  const finalize = async () => {
    setNextPrompt(false);
    const arr = pages;
    if (!arr.length) return;
    setBusy(true);
    const tid = toast.loading(`AI reading Kronos Z (${arr.length} page${arr.length > 1 ? "s" : ""})…`);
    try {
      const { data } = await api.post("/kronos/audit-z-report", {
        image_base64s: arr,
        shift_id: shift?.id || null,
        window_days: 7,
        commit_to_shift: commitToShift && !!shift,
      });
      data.matches
        ? toast.success("Kronos Z reconciled", { id: tid })
        : toast.error("Kronos Z mismatch detected", { id: tid });
      setLatest(data);
      loadAudits();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Kronos audit failed", { id: tid });
    } finally {
      setBusy(false);
      setPages([]);
    }
  };

  const cancel = () => {
    setNextPrompt(false);
    setPages([]);
  };

  return (
    <div
      data-testid="kronos-page"
      className="min-h-screen bg-slate-50 p-6 sm:p-10"
    >
      <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center shadow-lg">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900">
                KRONOS
              </h1>
              <p className="text-slate-500 tracking-wide mt-1 text-sm">
                Z-Report Reconciliation · Έλεγχος Κρόνου
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <input
            ref={camRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPageCaptured}
            className="hidden"
            data-testid="kronos-page-camera-input"
          />
          <Button
            onClick={() => {
              setPages([]);
              camRef.current?.click();
            }}
            disabled={busy}
            data-testid="kronos-page-scan-btn"
            className="min-h-[52px] px-6 bg-gradient-to-r from-violet-700 to-fuchsia-700 hover:from-violet-800 hover:to-fuchsia-800 text-white font-bold uppercase tracking-widest gap-2"
          >
            <Camera className="h-5 w-5" />
            {busy ? "AI auditing…" : "Scan Kronos Z-Report"}
          </Button>
          {shift && (
            <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={commitToShift}
                onChange={(e) => setCommitToShift(e.target.checked)}
                className="h-3.5 w-3.5 accent-violet-700"
                data-testid="kronos-page-commit-toggle"
              />
              <span>
                <b>+ ADD TO SHIFT</b> after audit
              </span>
            </label>
          )}
        </div>
      </header>

      {/* Multi-page prompt */}
      {nextPrompt && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4"
          data-testid="kronos-page-nextpage-overlay"
        >
          <Card className="max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-3">
              <ScanLine className="h-6 w-6 text-violet-700" />
              <h3 className="text-xl font-bold">Multi-Page Kronos Scan</h3>
            </div>
            <p className="text-sm text-slate-700 mb-4">
              📸 <b>Υπάρχει επόμενη σελίδα; / Is there another page?</b>
              <br />
              <span className="text-xs text-slate-500">
                Pages captured so far: <b data-testid="kronos-page-pages-count">{pages.length}</b>
              </span>
            </p>
            <div className="space-y-2">
              <Button
                onClick={askNext}
                className="w-full bg-violet-700 hover:bg-violet-800 text-white font-bold uppercase tracking-widest"
                data-testid="kronos-page-next-btn"
              >
                Ναι · Yes, scan next page
              </Button>
              <Button
                onClick={finalize}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase tracking-widest"
                data-testid="kronos-page-finalize-btn"
              >
                <Check className="h-4 w-4" />
                Όχι, ολοκλήρωση · Finalize
              </Button>
              <Button
                variant="outline"
                onClick={cancel}
                className="w-full"
                data-testid="kronos-page-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Latest audit summary */}
      {latest && (
        <Card
          className={`p-6 mb-6 border-2 ${
            latest.matches ? "border-emerald-300 bg-emerald-50/50" : "border-rose-400 bg-rose-50"
          }`}
          data-testid="kronos-page-latest"
        >
          <div className="flex items-center gap-2 mb-4">
            {latest.matches ? (
              <>
                <Check className="h-7 w-7 text-emerald-700" />
                <h2 className="text-2xl font-black text-emerald-800">
                  Kronos Z Reconciled · Συμφωνία
                </h2>
              </>
            ) : (
              <>
                <AlertTriangle className="h-7 w-7 text-rose-600 animate-pulse" />
                <h2 className="text-2xl font-black text-rose-700">
                  🚨 AI AUDIT ALERT · ΑΠΟΚΛΙΣΗ KRONOS Z!
                </h2>
              </>
            )}
            {latest.pages_count > 1 && (
              <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded">
                {latest.pages_count} pages
              </span>
            )}
            {latest.committed_count > 0 && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                +{latest.committed_count} lines → shift
              </span>
            )}
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-sm" data-testid="kronos-page-latest-table">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Stream</th>
                  <th className="text-right px-3 py-2">Z-Report</th>
                  <th className="text-right px-3 py-2">System</th>
                  <th className="text-right px-3 py-2">Δ Delta</th>
                </tr>
              </thead>
              <tbody>
                {latest.breakdown.map((r) => (
                  <tr
                    key={r.key}
                    className={
                      r.key === "grand"
                        ? "bg-slate-50 font-bold border-t-2 border-slate-300"
                        : r.matches
                        ? "bg-white"
                        : "bg-rose-50"
                    }
                  >
                    <td className="px-3 py-2 font-medium text-slate-800">{r.label}</td>
                    <td className="px-3 py-2 text-right font-mono">€{r.z_report_total.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">€{r.system_total.toFixed(2)}</td>
                    <td
                      className={`px-3 py-2 text-right font-mono font-bold ${
                        r.matches
                          ? "text-emerald-700"
                          : r.delta > 0
                          ? "text-amber-700"
                          : "text-rose-700"
                      }`}
                    >
                      {r.matches ? "✓" : `${r.delta >= 0 ? "+" : ""}€${r.delta.toFixed(2)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Audit history */}
      <Card className="p-6" data-testid="kronos-page-history">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="h-5 w-5 text-slate-700" />
          <h2 className="text-xl font-bold text-slate-900">
            Audit History · Ιστορικό Ελέγχων
          </h2>
          <span className="ml-auto text-xs text-slate-500">
            {audits.length} record{audits.length === 1 ? "" : "s"}
          </span>
        </div>
        {audits.length === 0 ? (
          <div
            className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center"
            data-testid="kronos-history-empty"
          >
            {/* Iter 109 · bilingual empty-state literal matching the
                Iter 106 Store Folders pattern — no static mock rows
                render when the KRONOS collection is empty. */}
            <div className="text-base font-semibold text-slate-700">
              Δεν βρέθηκαν καταχωρήσεις
            </div>
            <div className="text-sm text-slate-500 italic mt-1">
              No Kronos audits run yet. Scan your first Z-Report above.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kronos-page-history-table">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="text-center px-3 py-2">Pages</th>
                  <th className="text-right px-3 py-2">Z Grand</th>
                  <th className="text-right px-3 py-2">System</th>
                  <th className="text-right px-3 py-2">Δ</th>
                  <th className="text-right px-3 py-2">Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {audits.map((a) => {
                  const delta = (a.ocr_totals?.grand_total || 0) - (a.system_totals?.grand_total || 0);
                  return (
                    <tr key={a.id} className="border-t border-slate-100" data-testid={`kronos-history-row-${a.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">
                        {(a.report_date || a.created_at || "").slice(0, 10)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {a.matches ? (
                          <span className="text-emerald-700 font-bold">✓ Match</span>
                        ) : (
                          <span className="text-rose-700 font-bold">🚨 Mismatch</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center font-mono">{a.pages_count || 1}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        €{(a.ocr_totals?.grand_total || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        €{(a.system_totals?.grand_total || 0).toFixed(2)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono font-bold ${
                          Math.abs(delta) < 0.01
                            ? "text-emerald-700"
                            : delta > 0
                            ? "text-amber-700"
                            : "text-rose-700"
                        }`}
                      >
                        {Math.abs(delta) < 0.01 ? "✓" : `${delta >= 0 ? "+" : ""}€${delta.toFixed(2)}`}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {/* Iter 114 · Row-level audit delete */}
                        <button
                          type="button"
                          data-testid={`kronos-audit-delete-${a.id}`}
                          onClick={async () => {
                            if (!window.confirm("Θέλετε να διαγράψετε οριστικά αυτή την εγγραφή ιστορικού ελέγχου;")) return;
                            try {
                              await api.delete(`/kronos/audit/${a.id}`);
                              setAudits((prev) => prev.filter((r) => r.id !== a.id));
                            } catch (err) {
                              window.alert("Δεν ήταν δυνατή η διαγραφή: " + (err?.response?.data?.detail || err.message));
                            }
                          }}
                          title="Διαγραφή"
                          className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-700 hover:bg-rose-100"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
